import { useCallback, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  Building2,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  LayoutDashboard,
  PartyPopper,
  Plus,
  Rocket,
  ShoppingCart,
  Sparkles,
  Store,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { cn } from "lib/utils";

import { Button } from "js/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "js/components/ui/dialog";

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------
const STORAGE_KEY = "quota_onboarding_seen";

export function shouldShowQuotaTutorial(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== "true";
  } catch {
    return false;
  }
}

function markTutorialSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // localStorage may be unavailable (private browsing, etc.)
  }
}

// ---------------------------------------------------------------------------
// Step illustrations
// ---------------------------------------------------------------------------

function WelcomeIllustration() {
  return (
    <div className="relative flex h-full items-center justify-center">
      {/* Decorative floating rings */}
      <motion.div
        className="absolute h-32 w-32 rounded-full border-2 border-indigo-200/50"
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute h-44 w-44 rounded-full border border-violet-200/30"
        animate={{ scale: [1.1, 1, 1.1], opacity: [0.3, 0.5, 0.3] }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5,
        }}
      />

      {/* Central cart icon */}
      <motion.div
        className="relative z-10 flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow-lg shadow-indigo-200/50"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
      >
        <ShoppingCart className="h-10 w-10 text-indigo-600" strokeWidth={1.5} />
      </motion.div>

      {/* Floating accent: Zap */}
      <motion.div
        className="absolute -top-1 right-1/4 flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 shadow-md"
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, type: "spring" }}
      >
        <Zap className="h-5 w-5 text-amber-500" strokeWidth={2} />
      </motion.div>

      {/* Floating accent: Sparkles */}
      <motion.div
        className="absolute bottom-2 left-1/4 flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 shadow-md"
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6, type: "spring" }}
      >
        <Sparkles className="h-5 w-5 text-violet-500" strokeWidth={2} />
      </motion.div>
    </div>
  );
}

function NavigateIllustration() {
  const menuItems = [
    { icon: LayoutDashboard, label: "Dashboard", active: false },
    { icon: BarChart3, label: "Cuotas", active: true },
  ];

  return (
    <div className="flex h-full items-center justify-center">
      <motion.div
        className="w-52 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 180 }}
      >
        {/* Sidebar header */}
        <div className="border-b border-gray-100 px-4 py-3">
          <span className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
            Organización
          </span>
        </div>

        {/* Menu items */}
        {menuItems.map(({ icon: Icon, label, active }) => (
          <motion.div
            key={label}
            className={cn(
              "flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
              active
                ? "bg-indigo-50 font-medium text-indigo-700"
                : "text-gray-500",
            )}
            initial={active ? { x: -10, opacity: 0 } : undefined}
            animate={active ? { x: 0, opacity: 1 } : undefined}
            transition={active ? { delay: 0.5, type: "spring" } : undefined}
          >
            <Icon className={cn("h-4 w-4", active && "text-indigo-600")} />
            <span>{label}</span>
            {active && (
              <ChevronRight className="ml-auto h-4 w-4 text-indigo-400" />
            )}
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}

function BalanceIllustration() {
  const cards = [
    { label: "Persona Física", code: "PF", color: "from-indigo-500 to-indigo-600", delay: 0.2 },
    { label: "Persona Moral", code: "PM", color: "from-violet-500 to-violet-600", delay: 0.4 },
  ];

  return (
    <div className="flex h-full items-center justify-center gap-4">
      {cards.map(({ label, code, color, delay }) => (
        <motion.div
          key={code}
          className={cn(
            "relative flex h-28 w-36 flex-col justify-between overflow-hidden rounded-xl bg-gradient-to-br p-3.5 text-white shadow-lg",
            color,
          )}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay, type: "spring", stiffness: 200 }}
        >
          <div>
            <p className="text-[10px] font-medium uppercase opacity-80">
              {label}
            </p>
            <p className="mt-0.5 text-lg font-bold">{code}</p>
          </div>
          <div className="flex items-end justify-between">
            <span className="text-xs opacity-70">Créditos</span>
            <motion.div
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm"
              whileHover={{ scale: 1.1 }}
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} />
            </motion.div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function PaymentIllustration() {
  const methods = [
    { icon: CreditCard, label: "Tarjeta", color: "text-blue-600 bg-blue-50", delay: 0.2 },
    { icon: Building2, label: "SPEI", color: "text-emerald-600 bg-emerald-50", delay: 0.35 },
    { icon: Store, label: "Paynet", color: "text-orange-600 bg-orange-50", delay: 0.5 },
  ];

  return (
    <div className="flex h-full items-center justify-center gap-5">
      {methods.map(({ icon: Icon, label, color, delay }) => (
        <motion.div
          key={label}
          className="flex flex-col items-center gap-2"
          initial={{ y: 15, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay, type: "spring", stiffness: 200 }}
        >
          <div
            className={cn(
              "flex h-16 w-16 items-center justify-center rounded-2xl shadow-sm",
              color,
            )}
          >
            <Icon className="h-7 w-7" strokeWidth={1.5} />
          </div>
          <span className="text-xs font-medium text-gray-600">{label}</span>
        </motion.div>
      ))}
    </div>
  );
}

function CtaIllustration() {
  return (
    <div className="relative flex h-full items-center justify-center">
      {/* Confetti-like dots */}
      {[
        { x: "15%", y: "20%", size: 6, color: "bg-amber-400", d: 0.6 },
        { x: "80%", y: "15%", size: 5, color: "bg-indigo-400", d: 0.8 },
        { x: "10%", y: "75%", size: 4, color: "bg-violet-400", d: 1.0 },
        { x: "85%", y: "70%", size: 5, color: "bg-emerald-400", d: 0.7 },
        { x: "25%", y: "85%", size: 3, color: "bg-pink-400", d: 0.9 },
        { x: "70%", y: "85%", size: 4, color: "bg-blue-400", d: 1.1 },
      ].map(({ x, y, size, color, d }, i) => (
        <motion.div
          key={i}
          className={cn("absolute rounded-full", color)}
          style={{ left: x, top: y, width: size, height: size }}
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.2, 1] }}
          transition={{ delay: d, duration: 0.4 }}
        />
      ))}

      {/* Central rocket */}
      <motion.div
        className="relative z-10 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 shadow-xl shadow-indigo-300/40"
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
      >
        <Rocket className="h-9 w-9 text-white" strokeWidth={1.5} />
      </motion.div>

      {/* Party popper */}
      <motion.div
        className="absolute top-2 left-1/4 flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 shadow-md"
        initial={{ rotate: -20, opacity: 0 }}
        animate={{ rotate: 0, opacity: 1 }}
        transition={{ delay: 0.5, type: "spring" }}
      >
        <PartyPopper className="h-5 w-5 text-amber-500" />
      </motion.div>

      {/* Check circle */}
      <motion.div
        className="absolute right-1/4 bottom-4 flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 shadow-md"
        initial={{ rotate: 20, opacity: 0 }}
        animate={{ rotate: 0, opacity: 1 }}
        transition={{ delay: 0.7, type: "spring" }}
      >
        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Steps data
// ---------------------------------------------------------------------------

interface TutorialStep {
  title: string;
  description: string;
  illustration: () => React.JSX.Element;
}

const STEPS: TutorialStep[] = [
  {
    title: "Compra cuotas en autoservicio",
    description:
      "Ahora puedes comprar cuotas de consulta directamente desde la plataforma, sin intermediarios ni tiempos de espera.",
    illustration: WelcomeIllustration,
  },
  {
    title: "Encuentra tus cuotas",
    description:
      "En el menú lateral, busca la sección 'Organización' y haz clic en 'Cuotas' para acceder a tu centro de administración de créditos.",
    illustration: NavigateIllustration,
  },
  {
    title: "Visualiza y recarga tu saldo",
    description:
      "Verás tus créditos disponibles para Persona Física (PF) y Persona Moral (PM). Haz clic en 'Recargar' para iniciar tu compra.",
    illustration: BalanceIllustration,
  },
  {
    title: "Elige tu forma de pago",
    description:
      "Selecciona el paquete ideal y paga con tarjeta de crédito/débito, transferencia SPEI, o en efectivo en tiendas de conveniencia con Paynet.",
    illustration: PaymentIllustration,
  },
  {
    title: "Comienza ahora",
    description:
      "Tu primera recarga está a unos clics de distancia. Visita la sección de Cuotas y adquiere tus créditos de consulta.",
    illustration: CtaIllustration,
  },
];

const TOTAL_STEPS = STEPS.length;

// ---------------------------------------------------------------------------
// Motion variants
// ---------------------------------------------------------------------------

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 80 : -80,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? -80 : 80,
    opacity: 0,
  }),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface QuotaOnboardingTutorialProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function QuotaOnboardingTutorial({
  open,
  onOpenChange,
}: QuotaOnboardingTutorialProps): React.JSX.Element | null {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(0);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOTAL_STEPS - 1;

  const dismiss = useCallback(() => {
    markTutorialSeen();
    onOpenChange(false);
    // Reset to first step so re-opening starts fresh (if ever needed)
    setTimeout(() => setCurrentStep(0), 300);
  }, [onOpenChange]);

  const handleNext = useCallback(() => {
    if (isLastStep) return;
    setDirection(1);
    setCurrentStep((s) => s + 1);
  }, [isLastStep]);

  const handlePrev = useCallback(() => {
    if (isFirstStep) return;
    setDirection(-1);
    setCurrentStep((s) => s - 1);
  }, [isFirstStep]);

  const handleGoToQuotas = useCallback(() => {
    dismiss();
    navigate("/organizations/quotas");
  }, [dismiss, navigate]);

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (!value) {
        dismiss();
      } else {
        onOpenChange(value);
      }
    },
    [dismiss, onOpenChange],
  );

  const step = STEPS[currentStep];
  const Illustration = step.illustration;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="gap-0 overflow-hidden p-0 sm:max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {/* Illustration area */}
        <div className="relative h-[200px] overflow-hidden bg-gradient-to-br from-indigo-50 to-violet-50">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentStep}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <Illustration />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Content area */}
        <div className="flex flex-col px-6 pt-5 pb-6">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={`content-${currentStep}`}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeInOut" }}
            >
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {step.title}
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-relaxed text-gray-500">
                {step.description}
              </DialogDescription>
            </motion.div>
          </AnimatePresence>

          {/* Progress dots */}
          <div className="mt-6 flex items-center justify-center gap-1.5">
            {STEPS.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Ir al paso ${i + 1}`}
                onClick={() => {
                  setDirection(i > currentStep ? 1 : -1);
                  setCurrentStep(i);
                }}
                className={cn(
                  "h-2 rounded-full transition-all duration-300",
                  i === currentStep
                    ? "w-6 bg-indigo-600"
                    : "w-2 bg-gray-300 hover:bg-gray-400",
                )}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="mt-5 flex items-center justify-between">
            {/* Left side */}
            <div>
              {!isLastStep && (
                <Button
                  variant="link"
                  className="h-auto p-0 text-gray-400 hover:text-gray-600"
                  onClick={dismiss}
                >
                  Omitir
                </Button>
              )}
              {isLastStep && (
                <Button variant="outline" onClick={dismiss}>
                  Cerrar
                </Button>
              )}
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2">
              {!isFirstStep && (
                <Button variant="ghost" onClick={handlePrev}>
                  Anterior
                </Button>
              )}

              {!isLastStep && (
                <Button onClick={handleNext}>
                  Siguiente
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              )}

              {isLastStep && (
                <Button onClick={handleGoToQuotas}>
                  Ir a Cuotas
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
